import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";
import JsonLd from "@/components/feature/JsonLd";
import { SITE_URL } from "@/lib/seo";

const sectionKeys = ["s1", "s2", "s3", "s4"];
const faqKeys = [1, 2, 3];

export default function Article() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const meta = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const title = meta ? t(`article.${meta.slug}.title`) : "";
  const metaTitle = meta ? t(`article.${meta.slug}.metaTitle`) : "";
  const metaDesc = meta ? t(`article.${meta.slug}.metaDesc`) : "";

  if (!meta) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar solid />
        <main className="pt-16">
          <div className="mx-auto flex min-h-[60vh] w-full max-w-[820px] flex-col items-center justify-center px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("article.notFound")}
            </h1>
            <p className="mt-3 max-w-md text-sm text-foreground-600">{t("article.notFoundDesc")}</p>
            <Link
              to="/"
              className="mt-7 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              {t("article.backHome")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar solid />

      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: metaTitle || title,
          description: metaDesc,
          image: meta.image,
          datePublished: meta.updated,
          dateModified: meta.updated,
          inLanguage: i18n.language,
          author: { "@type": "Organization", name: t("nav.brand") },
          publisher: { "@type": "Organization", name: t("nav.brand") },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/${meta.slug}` },
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("article.breadcrumbHome"),
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: title,
              item: `${SITE_URL}/${meta.slug}`,
            },
          ],
        }}
      />

      <main className="pt-16">
        <article className="mx-auto w-full max-w-[820px] px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-1 text-xs text-foreground-500 md:text-sm" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-primary-600">
              {t("article.breadcrumbHome")}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-700">{t(`article.${meta.slug}.category`)}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              {t(`article.${meta.slug}.category`)}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {t(`article.${meta.slug}.title`)}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
              {t(`article.${meta.slug}.subtitle`)}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-line text-sm"></i>
                </span>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </span>
                {t("article.updated")}: {meta.updated}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line text-sm"></i>
                </span>
                {t("article.readTime")}
              </span>
            </div>
          </header>

          <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
            <img
              src={meta.image}
              alt={t(`article.${meta.slug}.title`)}
              title={`${t(`article.${meta.slug}.title`)} — USFans Spreadsheet`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-foreground-800">
              {t("article.toc")}
            </h2>
            <ol className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {sectionKeys.map((key, index) => (
                <li key={key}>
                  <a
                    href={`#section-${index + 1}`}
                    className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                  >
                    <span className="font-semibold text-primary-600">{index + 1}.</span>
                    <span>{t(`article.${meta.slug}.${key}.title`)}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          <p className="mt-8 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t(`article.${meta.slug}.intro`)}
          </p>

          <div className="mt-8 flex flex-col gap-10">
            {sectionKeys.map((key, index) => (
              <section key={key} id={`section-${index + 1}`}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {t(`article.${meta.slug}.${key}.title`)}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {t(`article.${meta.slug}.${key}.p1`)}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {t(`article.${meta.slug}.${key}.p2`)}
                </p>
              </section>
            ))}
          </div>

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.faqTitle")}
            </h2>
            <div className="mt-5 flex flex-col gap-3">
              {faqKeys.map((num) => (
                <div key={num} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 md:text-base">
                    {t(`article.${meta.slug}.faq.q${num}`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {t(`article.${meta.slug}.faq.a${num}`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg bg-primary-100/70 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t(`article.${meta.slug}.conclusion.title`)}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t(`article.${meta.slug}.conclusion.p1`)}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t(`article.${meta.slug}.conclusion.p2`)}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="rounded-full bg-primary-500 px-6 py-3 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                {t("article.backHome")}
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="rounded-full border border-primary-300 px-6 py-3 text-center text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 whitespace-nowrap"
              >
                {t("cta.secondary")}
              </a>
            </div>
          </section>

          <div className="mt-10 border-t border-background-200 pt-6">
            <h2 className="font-heading text-base font-semibold text-foreground-800">
              {t("article.relatedTitle")}
            </h2>
            <div className="mt-3 flex flex-col gap-2">
              {articles
                .filter((a) => a.slug !== meta.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/${a.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-article-line text-sm"></i>
                    </span>
                    {t(`article.${a.slug}.title`)}
                  </Link>
                ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}